// The first-day setup steps — ONE list read by both places that show it
// (F19 UF-01): the Dashboard's "Finish setting up your packhouse" card and
// Config → Setup's "Getting started". Before this the Home list and Config's
// Health tab were two setup doctors that did not know about each other.
//
// Rules (moved here whole from FirstRunChecklist):
//   * Admins and managers only (isManagerOrAbove). A step only an admin can do
//     (rate cards — the financial_products write policies are ADMIN-only — and
//     inviting the team) says "An admin does this" to a manager, with no link.
//   * Each step reads whether it is REALLY done (firstRunChecks.ts — D1). A
//     read that fails is 'unknown': "Couldn't check", never a tick or a zero.
//   * A Config step opens its exact section (packhouseConfigHref → ?section=)
//     — offered as a link only to someone /config admits
//     (useCanOpenPackhouseConfig, Jason 3 Oct — see LoadingBay). A custom
//     MANAGER-based role without the config module is bounced to Home by the
//     route, so to them the row names who does it, with no link; while the
//     gate is still deciding, no link either.
//   * A step the business marked "Not needed here" (UF-30 — one system_config
//     row for the business, so every PC agrees) counts as done. The read is
//     here; the control and its write live on Config → Setup only.
//
// Both callers share the query keys, so opening Config after Home costs no
// second read of the same step.

import { useMemo } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import {
  ChevronRight, CheckCircle2, Circle, Building2, DollarSign,
  Users, Truck, Globe, Map as MapIcon, Sprout, UserPlus, AlertTriangle,
  Cherry, Layers, Snowflake, Thermometer, Package, Printer, MinusCircle,
} from 'lucide-react'
import clsx from 'clsx'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { isAdmin, isManagerOrAbove } from '@/lib/roles'
import { packhouseConfigHref } from '@/lib/packhouseConfigHref'
import { readState, type ReadState, type ReadStateLike } from '@/lib/readState'
import { useFlowManifest, useFlowManifests } from '@/hooks/useCommodityTemplates'
import { useTenantCommodityRows } from '@/hooks/useCommodities'
import { supabase } from '@/lib/supabase'
import { useCanOpenPackhouseConfig } from '@/hooks/useCanOpenPackhouseConfig'
import {
  SETUP_CHECKS, FORCE_COOL_CHECKS, runSetupCheck, identityHint, hasAnyPallet, readNotNeeded,
  type SetupCheckKey, type SetupCheckResult, type StepState,
} from './firstRunChecks'

/** The DOM id of the pack-format finding card on Config → Setup (D7). */
export const PACK_FORMAT_FINDING_ID = 'finding-pack-format-unpriced'

export interface SetupStepRow {
  key:    string
  label:  string
  hint:   string
  /** Where the step is done — a Config step names its exact section. */
  to:     string
  icon:   typeof Building2
  /** Only a business admin can finish it (the database refuses a manager). */
  adminOnly?: boolean
  /** 'done' only on a read that worked and found the step finished; a read
   *  that failed is 'unknown' — never a tick, never a zero (D1). */
  state:  StepState
  optional?: boolean
  /** The business marked it "Not needed here" and it is not done: it counts
   *  as done, and lists as "Not needed" with an Undo on Config → Setup. */
  notNeeded?: boolean
  /** D7: the id of a block ON Config → Setup that this step's figure points at.
   *  Config → Setup scrolls to it instead of opening `to` (the page it is
   *  already on); the Dashboard card ignores it and opens `to`. */
  scrollToId?: string
}

/** Done, or settled as not needed — either way it no longer asks anything. */
export const stepSettled = (r: SetupStepRow): boolean => r.state === 'done' || !!r.notNeeded

/** Shared by the read below and the write on Config → Setup. */
export const notNeededQueryKey = (tenantId: string | null) =>
  ['first-run-checklist', tenantId, 'not-needed'] as const

export interface SetupSteps {
  /** An admin or manager — nobody else is shown the steps or reads them. */
  canSee:   boolean
  admin:    boolean
  /** Would /config let this person in? null while its gates are deciding —
   *  a Config step (and "Open setup") is a link only on `true`. */
  canOpenConfig: boolean | null
  /** true = no pallet yet; false = has packed; null = pending or failed read. */
  firstDays: boolean | null
  /** The pallet read itself — tells a pending null from a failed one. */
  palletRead: ReadState
  /** The "not needed here" read: a step may be marked or undone only when
   *  this is 'usable' — a write replaces the whole list. */
  notNeededRead: ReadState
  /** The keys that read returned ([] until it is usable). */
  notNeededKeys: string[]
  /** The query behind them, for the write's refuseUnreadSave. */
  notNeededQuery: ReadStateLike
  rows:     SetupStepRow[]
  doneCount:    number
  /** Required steps (optional ones never count towards "n of m"). */
  totalCount:   number
  /** Required steps not yet done (incl. ones that couldn't be checked). */
  openCount:    number
  unknownCount: number
  isLoading:    boolean
  allDone:      boolean
}

/**
 * `firstDaysOnly`: the Home card reads the steps only while the business has
 * no pallet — a live business pays one one-row read per Home visit. Config →
 * Setup reads them whenever it is opened (folded under the checks there).
 */
export function useSetupSteps({ firstDaysOnly }: { firstDaysOnly: boolean }): SetupSteps {
  const { data: tenant } = useActiveTenant()
  const tenantId = tenant?.tenantId ?? null
  const { data: me } = useCurrentUser()
  const canSee   = isManagerOrAbove(me?.role)
  const admin    = isAdmin(me?.role)
  const canOpenConfig = useCanOpenPackhouseConfig()
  // Blueberry packhouses don't run the PPECB exporter flow → drop that step;
  // a force-cool (berry) flow adds its cooling + carton steps (UF-02).
  const { usesPPECB, usesForceCooling } = useFlowManifest()

  // Has this business packed yet? One row answers it; a failed read leaves
  // firstDays null, and the Home card then stays hidden rather than guessing.
  const palletQ = useQuery({
    queryKey: ['first-run-checklist', tenantId, 'has-pallet'],
    enabled:  !!tenantId && canSee,
    staleTime: 5 * 60_000,
    queryFn:  () => hasAnyPallet(tenantId as string),
  })
  const firstDays = palletQ.data === undefined ? null : !palletQ.data

  // One query per step, keyed on the tenant: a failed read marks ITS row
  // "Couldn't check" and leaves the others honest, and a tenant switch never
  // serves the previous business's ticks.
  const results = useQueries({
    queries: SETUP_CHECKS.map(key => ({
      queryKey: ['first-run-checklist', tenantId, key],
      enabled:  !!tenantId && canSee && (!firstDaysOnly || firstDays === true)
        && (usesForceCooling || !FORCE_COOL_CHECKS.includes(key)),
      staleTime: 30_000,
      queryFn:  () => runSetupCheck(key, tenantId as string),
    })),
  })
  // Which steps this business marked "Not needed here" (UF-30). A failed read
  // marks none — every step then shows its real state — and Config → Setup
  // offers no control until it reads.
  const notNeededQ = useQuery({
    queryKey: notNeededQueryKey(tenantId),
    enabled:  !!tenantId && canSee && (!firstDaysOnly || firstDays === true),
    staleTime: 30_000,
    queryFn:  () => readNotNeeded(tenantId as string),
  })
  const notNeededRead = readState(notNeededQ)
  const notNeededKeys = notNeededRead === 'usable' ? notNeededQ.data ?? [] : []

  const stBase = (k: SetupCheckKey): StepState => stepStateOf(results[SETUP_CHECKS.indexOf(k)])
  const identity: SetupCheckResult | undefined = results[SETUP_CHECKS.indexOf('identity')].data
  const rates: SetupCheckResult | undefined = results[SETUP_CHECKS.indexOf('rates')].data
  const { st, cartonFruit } = useCartonStepState(stBase, canSee, usesForceCooling)
  const rows = setupStepRows(st, identity, rates, usesPPECB, usesForceCooling, notNeededKeys, cartonFruit)
  return {
    canSee, admin, canOpenConfig, firstDays, palletRead: readState(palletQ), notNeededRead, notNeededKeys,
    notNeededQuery: notNeededQ,
    ...summariseSteps(rows, notNeededRead),
  }
}

/** The CARTON step's state, folded with the fruit that lacks the unit (UF-05 / D-12). */
function useCartonStepState(stBase: (k: SetupCheckKey) => StepState, canSee: boolean, usesForceCooling: boolean) {
  const cartonBase = stBase('cartonUnit')
  const cartonFruit = useCartonlessFruit(
    usesForceCooling && canSee && (cartonBase === 'open' || cartonBase === 'done'), usesForceCooling)
  const st = (k: SetupCheckKey): StepState => k === 'cartonUnit' ? cartonUnitState(cartonBase, cartonFruit) : stBase(k)
  return { st, cartonFruit }
}

/**
 * U29 UF-05 / D-12: the first fruit this business handles, on a force-cool
 * flow, that has no active CARTON pack unit (pure; list order = the page's).
 * A fruit with no flow of its own follows the business's flow.
 */
export function firstCartonlessFruit(
  fruits: { commodity_id: string; flow_key: string | null }[],
  handledIds: ReadonlySet<string>,
  cartonIds: ReadonlySet<string>,
  forceCoolOf: (flowKey: string | null) => boolean,
): string | null {
  const f = fruits.find(x => handledIds.has(x.commodity_id) && !cartonIds.has(x.commodity_id)
    && forceCoolOf(x.flow_key))
  return f?.commodity_id ?? null
}

/** Reads behind the CARTON step's link. Any read not usable = null = the bare link. */
function useCartonlessFruit(enabled: boolean, tenantForceCools: boolean): string | null {
  const { data: tenant } = useActiveTenant()
  const tenantId = tenant?.tenantId ?? null
  const on = !!enabled && !!tenantId
  const handledQ = useTenantCommodityRows(on ? tenantId : null)
  const fruitsQ = useQuery({
    queryKey: ['setup-commodity-flows'], enabled: on, staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('commodities')
        .select('commodity_id, flow_key').order('sort_order').order('name')
      if (error) throw error
      return (data ?? []) as { commodity_id: string; flow_key: string | null }[]
    },
  })
  const cartonQ = useQuery({
    queryKey: ['setup-carton-units', tenantId], enabled: on, staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('commodity_pack_units')
        .select('commodity_id').eq('tenant_id', tenantId as string)
        .eq('unit_code', 'CARTON').eq('is_active', true)
      if (error) throw error
      return (data ?? []) as { commodity_id: string }[]
    },
  })
  const flowKeys = useMemo(
    () => [...new Set((fruitsQ.data ?? []).map(f => f.flow_key).filter((k): k is string => !!k))],
    [fruitsQ.data])
  const manifests = useFlowManifests(flowKeys)
  if (!on || !handledQ.isSuccess || !fruitsQ.isSuccess || !cartonQ.isSuccess) return null
  const forceCool = new Map(flowKeys.map((k, i) => [k, manifests[i].usesForceCooling]))
  return firstCartonlessFruit(
    fruitsQ.data,
    new Set(handledQ.data.map(h => h.commodity_id)),
    new Set(cartonQ.data.map(c => c.commodity_id)),
    k => k === null ? tenantForceCools : forceCool.get(k) === true)
}

/**
 * U29 D-12: CARTON is done only when every handled force-cool fruit has the
 * unit. The any-fruit head count says done; a fruit that lacks it reopens the
 * step. An unreadable fruit read (null) leaves the head-count verdict as is.
 */
export function cartonUnitState(base: StepState, cartonlessFruit: string | null): StepState {
  return base === 'done' && cartonlessFruit ? 'open' : base
}

/** The CARTON step's target: the fruit that lacks the unit, at Pack units; else bare. */
export function cartonUnitHref(fruitId: string | null): string {
  return fruitId ? `/commodities?commodity=${encodeURIComponent(fruitId)}&list=pack_units` : '/commodities'
}

/** One step query's answer as a row state — a failed read is 'unknown' (D1). */
function stepStateOf(q: ReadStateLike & { data?: SetupCheckResult }): StepState {
  const rs = readState(q)
  if (rs === 'usable') return q.data?.done ? 'done' : 'open'
  return rs === 'unread' ? 'unknown' : 'loading'
}

/** The identity row — the PHC is only asked of a business that runs the PPECB flow. */
function identityRow(st: (k: SetupCheckKey) => StepState, identity: SetupCheckResult | undefined,
  usesPPECB: boolean): SetupStepRow {
  const phcMissing = usesPPECB && !identity?.phcSet
  const state = st('identity')
  return { key: 'identity',    icon: Building2,  label: 'Set packhouse identity',
    hint:   identityHint(identity, usesPPECB),
    to:     packhouseConfigHref('business', identity?.legalNameSet && phcMissing
      ? 'PPECB identity & defaults' : 'Letterhead / branding'),
    state:  state === 'done' && phcMissing ? 'open' : state }
}

export function setupStepRows(st: (k: SetupCheckKey) => StepState, identity: SetupCheckResult | undefined,
  rates: SetupCheckResult | undefined,
  usesPPECB: boolean, usesForceCooling: boolean, notNeededKeys: string[], cartonFruit: string | null = null): SetupStepRow[] {
  const rows: SetupStepRow[] = [
    identityRow(st, identity, usesPPECB),
    // The catalogue (sizes, grades, pack formats) is empty until a fruit is
    // ticked — ticking it fills them in one go (F19 verify-walk V1).
    { key: 'fruit',       icon: Cherry,     label: 'Pick the fruit you pack',
      hint:   'Tick each fruit you handle — that fills its sizes, grades, pack formats and intake sizes. You add the varieties.',
      to:     packhouseConfigHref('commodities'),
      state:  st('fruit') },
    { key: 'rates',       icon: DollarSign, label: 'Add pack types & rate cards',
      hint:   rates?.formats && rates.formats.total > 0
        ? `${rates.formats.withCard} of ${rates.formats.total} pack formats have a card.`
        : usesPPECB
        ? 'Each pack-type carton spec (7kg open-top, 15kg telescopic, …) plus its per-unit price — drives invoice line totals.'
        : 'Each pack SKU (125 g punnet, 150 g clamshell, …) plus its per-unit price — drives invoice line totals.',
      // With the N-of-M figure shown the step opens Config -> Setup, where the
      // pack formats are listed (RB1 D6); with no figure it opens the rate cards.
      // Every format has a card: the finding below does not exist, so go to the cards (D7).
      to:     rates?.formats && rates.formats.total > rates.formats.withCard ? packhouseConfigHref('health') : '/financial?tab=rate_card',
      scrollToId: rates?.formats && rates.formats.total > rates.formats.withCard ? PACK_FORMAT_FINDING_ID : undefined,
      adminOnly: true,
      state:  st('rates') },
    { key: 'growers',     icon: Sprout,     label: 'Add your first grower',
      hint:   'PUC + block tree. You can also link a farm-tenant via /admin once they sign up.',
      to:     '/growers',
      state:  st('growers') },
    { key: 'customers',   icon: Users,      label: 'Add customers',
      hint:   'Who you ship to — appears on consignment notes + invoices.',
      to:     packhouseConfigHref('dispatch', 'Destinations'),
      state:  st('customers') },
    { key: 'transporters', icon: Truck,     label: 'Add transporters',
      hint:   'Trucking companies you use for dispatch.',
      to:     packhouseConfigHref('dispatch', 'Transporters'),
      state:  st('transporters') },
    { key: 'exporters',   icon: Globe,      label: 'Add exporters',
      hint:   'Your export partners (PPECB exporter codes go on each).',
      to:     packhouseConfigHref('dispatch', 'Exporters'),
      state:  st('exporters'),
      optional: true },
    // Every country is already pickable (the shared baseline), so having
    // markets proves nothing — the step is putting YOURS first (D1).
    { key: 'markets',     icon: Globe,      label: 'Put your markets first',
      hint:   'Every country is already pickable — order or hide them so the markets you ship to come first.',
      to:     packhouseConfigHref('dispatch', 'Target markets'),
      state:  st('markets'),
      optional: true },
    { key: 'lanes',       icon: MapIcon,    label: 'Configure loading-bay lanes',
      hint:   'Physical staging slots for dispatch.',
      to:     packhouseConfigHref('storage', 'Loading Bay Lanes'),
      state:  st('lanes'),
      optional: true },
    { key: 'presets',     icon: Layers,     label: 'Add line presets',
      hint:   'The pack specs operators pick on a run to make pallets pre-filled.',
      to:     packhouseConfigHref('operations', 'Line presets'),
      state:  st('presets') },
    { key: 'coldrooms',   icon: Snowflake,  label: 'Add coldrooms',
      hint:   'Each room and its grid — where pallets are put away.',
      to:     packhouseConfigHref('storage', 'Coldrooms'),
      state:  st('coldrooms') },
    // Force-cool (berry) flows only — filtered out below otherwise.
    { key: 'coolingRooms', icon: Snowflake, label: 'Map the cooling flow to rooms',
      hint:   'Which coldroom each force-cool stage uses — the Force-Cooling page shows where each batch sits.',
      to:     packhouseConfigHref('storage', 'Cooling flow'),
      state:  st('coolingRooms') },
    { key: 'coolingDefaults', icon: Thermometer, label: 'Set cooling defaults',
      hint:   'The standard gross weight of a full crate per variety — batch weights are suggested from it.',
      to:     packhouseConfigHref('storage', 'Cooling defaults'),
      state:  st('coolingDefaults') },
    { key: 'cartonUnit',  icon: Package,    label: 'Add the CARTON pack unit',
      hint:   'Commodities → your fruit → Pack units: the net kg per carton the berry label prints as NET/CARTON.',
      to:     cartonUnitHref(cartonFruit),
      state:  st('cartonUnit') },
    // Done once a PC of this business has printed a label; the station's
    // name is set on Printing setup and stamped on every print.
    { key: 'printing',    icon: Printer,    label: 'Set up a print station',
      hint:   'Name this PC and pick its printer and label — no PC has printed a label yet.',
      to:     '/printing-setup',
      state:  st('printing') },
    { key: 'members',     icon: UserPlus,   label: 'Invite your team',
      hint:   'Scale operators, QC, dispatchers, loaders — anyone who needs an account.',
      to:     '/users',
      adminOnly: true,
      state:  st('members') },
  // Hide the PPECB exporter step for flows that don't use it (e.g. blueberry),
  // and the cooling + carton steps for flows that don't force-cool (citrus).
  ]
  return rows.filter(r => (usesPPECB || r.key !== 'exporters')
    && (usesForceCooling || !FORCE_COOL_CHECKS.includes(r.key as SetupCheckKey)))
    .map(r => r.state !== 'done' && notNeededKeys.includes(r.key) ? { ...r, notNeeded: true } : r)
}

type StepSummary = Pick<SetupSteps,
  'rows' | 'doneCount' | 'totalCount' | 'openCount' | 'unknownCount' | 'isLoading' | 'allDone'>

function summariseSteps(rows: SetupStepRow[], notNeededRead: ReadState): StepSummary {
  const required     = rows.filter(r => !r.optional)
  const doneCount    = required.filter(stepSettled).length
  const isLoading    = rows.some(r => r.state === 'loading') || notNeededRead === 'loading'
  return {
    rows, doneCount,
    totalCount:   required.length,
    openCount:    required.length - doneCount,
    unknownCount: rows.filter(r => r.state === 'unknown' && !r.notNeeded).length,
    isLoading,
    allDone:      doneCount === required.length && !isLoading,
  }
}

/** The rows themselves — the same markup on Home and on Config → Setup. */
export function SetupStepList({ rows, admin, canOpenConfig, onOpen, onNotNeeded, onUndoNotNeeded, busy }: {
  rows:   SetupStepRow[]
  admin:  boolean
  /** useSetupSteps' `canOpenConfig` — a Config step links only on `true`. */
  canOpenConfig: boolean | null
  onOpen: (to: string, r: SetupStepRow) => void
  /** Config → Setup only, for a business ADMIN or MANAGER once the marks have
   *  read: an open required step offers "Not needed here". */
  onNotNeeded?:     (r: SetupStepRow) => void
  /** Same reader: a step marked not needed offers "Undo". */
  onUndoNotNeeded?: (r: SetupStepRow) => void
  /** A mark is being written — the controls wait for it. */
  busy?: boolean
}) {
  return (
    <ul className="space-y-1">
      {rows.map(r => (
        <SetupStepItem key={r.key} r={r} admin={admin} canOpenConfig={canOpenConfig} onOpen={onOpen}
          onNotNeeded={onNotNeeded} onUndoNotNeeded={onUndoNotNeeded} busy={busy} />
      ))}
    </ul>
  )
}

function StepStatusIcon({ notNeeded, done, unknown }: { notNeeded: boolean; done: boolean; unknown: boolean }) {
  if (notNeeded) return <MinusCircle   size={18} className="text-muted shrink-0" aria-label="Not needed" />
  if (done)      return <CheckCircle2  size={18} className="text-emerald-400 shrink-0" aria-label="Done" />
  if (unknown)   return <AlertTriangle size={18} className="text-amber-400 shrink-0" aria-label="Couldn't check" />
  return <Circle size={18} className="text-muted shrink-0" aria-label="Not done" />
}

function stepSubtitle(r: SetupStepRow, f: {
  notNeeded: boolean; unknown: boolean; forAdmin: boolean; noConfig: boolean; done: boolean
}): string {
  if (f.notNeeded) return 'Not needed here — marked for this business, on every PC.'
  if (f.unknown)   return "Couldn't check — the read failed. Open it to see where it stands."
  if (f.forAdmin && !f.done) return 'An admin does this step — ask your business admin.'
  if (f.noConfig && !f.done) return 'Set on the Config page, which your role does not open — ask your business admin.'
  return r.hint
}

/** `inert`: the row opens nothing (admin-only for a manager, or a Config
 *  step the viewer can't open) — no hover, since there is nothing to tap. */
function stepRowClass(inert: boolean, done: boolean): string {
  return clsx(
    'w-full text-left px-2 py-2 rounded-lg flex items-center gap-3 transition-colors',
    inert ? null
      : done ? 'opacity-60 hover:opacity-100 hover:bg-emerald-900/20'
      : 'hover:bg-emerald-900/30',
    inert && done && 'opacity-60',
  )
}

const MARK_CLS = 'tap-target-y shrink-0 text-[11px] font-semibold px-2 py-1 rounded border '
  + 'border-line-strong text-fg-secondary hover:border-emerald-500 hover:text-emerald-200 disabled:opacity-50'

function SetupStepItem({ r, admin, canOpenConfig, onOpen, onNotNeeded, onUndoNotNeeded, busy }: {
  r:      SetupStepRow
  admin:  boolean
  canOpenConfig: boolean | null
  onOpen: (to: string, r: SetupStepRow) => void
  onNotNeeded?:     (r: SetupStepRow) => void
  onUndoNotNeeded?: (r: SetupStepRow) => void
  busy?: boolean
}) {
  const f = stepFlags(r, admin, canOpenConfig)
  const rowCls = stepRowClass(!f.opens, f.done)
  const body = <StepBody r={r} {...f} />
  // The mark control: "Not needed here" on an open required step, "Undo" on a
  // marked one — each only where the caller passed its handler.
  const mark = f.notNeeded
    ? onUndoNotNeeded && { act: onUndoNotNeeded, text: 'Undo', name: 'undo not needed' }
    : stepOffersNotNeeded(r) && onNotNeeded && { act: onNotNeeded, text: 'Not needed here', name: 'not needed here' }
  return (
    <li className="flex items-center gap-1">
      <div className="flex-1 min-w-0">
        {f.opens
          ? <button onClick={() => onOpen(r.to, r)} className={rowCls}>{body}</button>
          : <div className={rowCls}>{body}</div>}
      </div>
      {mark && (
        <button type="button" onClick={() => mark.act(r)} disabled={busy}
          aria-label={`${r.label}: ${mark.name}`} className={MARK_CLS}>
          {mark.text}
        </button>
      )}
    </li>
  )
}

/** A step done on the packhouse Config page (a packhouseConfigHref). */
const isConfigStep = (r: Pick<SetupStepRow, 'to'>): boolean => r.to.startsWith('/config?')

/** How one row reads. A manager can't finish an admin-only step (the database
 *  refuses the write, the route gate refuses /users): `forAdmin` says who does
 *  it, with no link. A Config step for someone /config does not admit
 *  (`noConfig`) says the same; while that gate is deciding (`canOpenConfig`
 *  null) the row shows its hint with no link — never one that may bounce. */
function stepFlags(r: SetupStepRow, admin: boolean, canOpenConfig: boolean | null) {
  const notNeeded = !!r.notNeeded
  const forAdmin  = !!r.adminOnly && !admin
  const config    = isConfigStep(r)
  return {
    notNeeded,
    done:     r.state === 'done' || notNeeded,
    unknown:  r.state === 'unknown' && !notNeeded,
    forAdmin,
    noConfig: config && canOpenConfig === false,
    opens:    !forAdmin && (!config || canOpenConfig === true),
  }
}

/** An open, required, unmarked step may be marked "Not needed here". */
const stepOffersNotNeeded = (r: SetupStepRow): boolean => !r.optional && r.state === 'open' && !r.notNeeded

/** A step's icon, label and one-line hint — the part that opens it. */
function StepBody({ r, notNeeded, done, unknown, forAdmin, noConfig, opens }: {
  r: SetupStepRow; notNeeded: boolean; done: boolean; unknown: boolean; forAdmin: boolean
  noConfig: boolean; opens: boolean
}) {
  return (
    <>
      <StepStatusIcon notNeeded={notNeeded} done={done} unknown={unknown} />
      <r.icon size={14} className={clsx('shrink-0', done ? 'text-muted' : 'text-emerald-300')} />
      <div className="flex-1 min-w-0">
        <p className={clsx(
          'text-sm font-medium truncate',
          notNeeded ? 'text-fg-muted' : done ? 'text-fg-muted line-through' : 'text-fg',
        )}>
          {r.label}
          {r.optional && !done && (
            <span className="ml-1.5 text-[10px] text-muted font-normal">(optional)</span>
          )}
        </p>
        <p className={clsx('text-[11px] truncate', unknown ? 'text-amber-300' : 'text-muted')}>
          {stepSubtitle(r, { notNeeded, unknown, forAdmin, noConfig, done })}
        </p>
      </div>
      {!done && opens && <ChevronRight size={14} className="text-muted shrink-0" />}
    </>
  )
}
